import * as React from 'react';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { CohortSendArgs } from 'bundles/enterprise-admin-messaging-v2/hooks/useAICohortFlow';
import type { PreviewMessageResult, SendArgs } from 'bundles/enterprise-admin-messaging-v2/hooks/useSendMessage';

type CohortFlowResolveSendArgs = {
  resolveSendArgs: (
    ctaText: string,
    audienceDelta?: { removedIds: string[]; addedIds: string[] }
  ) => CohortSendArgs | null;
};

type UseCohortPreviewArgs = {
  draft: AIMessageDraft;
  cohortFlow: CohortFlowResolveSendArgs;
  previewMessage: (args: SendArgs & { draft: AIMessageDraft }) => Promise<PreviewMessageResult>;
};

/**
 * Encapsulates the server-rendered email preview for the NLQ cohort (generic AI
 * compose) flow. Resolves `CohortSendArgs` from the cohort flow and delegates
 * to `previewMessage` (which creates a throwaway draft and fetches its rendered
 * HTML from the backend).
 *
 * Isolated from the existing filter/selection/manual CSV preview path so the two
 * cannot regress each other.
 */
const useCohortPreview = ({ draft, cohortFlow, previewMessage }: UseCohortPreviewArgs) => {
  const fetchPreviewHtml = React.useCallback(async (): Promise<string> => {
    const sendArgs = cohortFlow.resolveSendArgs(draft.ctaText, draft.audienceDelta);
    if (!sendArgs) throw new Error('Cohort is not ready for preview.');
    const result = await previewMessage({ draft, ...sendArgs });
    if (result.status !== 'success') throw new Error(result.error);
    return result.html;
  }, [draft, cohortFlow, previewMessage]);

  const isReady = Boolean(draft.cohortId);

  return { fetchPreviewHtml, isReady };
};

export default useCohortPreview;
